/**
 * Build transaction metadata for initiation.
 *
 * @param {Object} params - Order params or transition params.
 * @param {Object} [fallbackParams] - Secondary params for fallback values.
 * @returns {{ metadata: { startDate?: string, endDate?: string, price?: number, sessionId?: string } } | {}}
 */
export const buildTransactionMetadataMaybe = (params, fallbackParams) => {
  const sources = [params, fallbackParams].filter(Boolean);
  if (sources.length === 0) {
    return {};
  }

  const bookingStart = resolveFirstValue(sources, source =>
    source.metadata?.startDate ||
      source.startDate ||
      source.bookingStart ||
      source.bookingDates?.bookingStart ||
      source.bookingStartTime
  );
  const bookingEnd = resolveFirstValue(sources, source =>
    source.metadata?.endDate ||
      source.endDate ||
      source.bookingEnd ||
      source.bookingDates?.bookingEnd ||
      source.bookingEndTime
  );

  const startDate = normalizeDateForMetadata(bookingStart);
  const endDate = normalizeDateForMetadata(bookingEnd);

  const price = resolveFirstValue(sources, source =>
    resolvePriceForMetadata(source) ?? source.metadata?.price
  );
  const sessionId = resolveFirstValue(
    sources,
    source => source.sessionId || source.metadata?.sessionId
  );

  const metadata = {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(Number.isInteger(price) ? { price } : {}),
    ...(sessionId ? { sessionId } : {}),
  };

  return Object.keys(metadata).length > 0 ? { metadata } : {};
};

const resolveFirstValue = (sources, getter) => {
  for (const source of sources) {
    const value = getter(source);
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return null;
};

const normalizeDateForMetadata = value => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Number.isInteger(value)) {
    return new Date(value).toISOString();
  }

  if (typeof value === 'string') {
    return value;
  }

  return null;
};

const resolvePriceForMetadata = params => {
  const priceCandidates = [
    params?.unitPriceInSubunits,
    params?.priceInSubunits,
    params?.price,
    params?.unitPrice,
  ];

  const firstValid = priceCandidates.find(value => Number.isInteger(value));
  return Number.isInteger(firstValid) ? firstValid : null;
};
